import React from 'react';
import type { Case, TradeDetails } from 'types/interfaces/case';
import type { MessageImage } from 'types/interfaces/message';
import { caseReasonLongLabel, NO_REASON_LABEL } from 'constants/caseReasons';

interface Props {
  caseDetails: Case & { trade: TradeDetails };
  onOpenImage: (images: MessageImage[], index: number) => void;
}

const SOURCE_LABEL: Record<string, string> = {
  SENDING: 'Sending',
  RECEIVING: 'Receiving',
  INBOX: 'Inbox',
};

const formatTimestamp = (iso: string): string =>
  new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });

const daysAfter = (fromIso: string | null | undefined, toIso: string): number | null => {
  if (!fromIso) return null;
  const from = Date.parse(fromIso);
  const to = Date.parse(toIso);
  if (Number.isNaN(from) || Number.isNaN(to)) return null;
  return Math.max(0, Math.floor((to - from) / (24 * 60 * 60 * 1000)));
};

/**
 * "Why you were summoned", the first panel on the admin case detail (Asana
 * 1219952262403623, Decision 22). Legacy cases, which carry reason null, show
 * only the no-reason line.
 */
export const WhySummonedPanel: React.FC<Props> = ({ caseDetails, onOpenImage }) => {
  const { trade } = caseDetails;
  const reason = caseDetails.reason ?? null;
  const role = caseDetails.summonedByRole;
  const username =
    caseDetails.summonedByUser?.username ??
    (role === 'SENDER' ? trade.sender.username : role === 'RECEIVER' ? trade.receiver.username : null);
  const otherParty = role === 'SENDER' ? trade.receiver.username : role === 'RECEIVER' ? trade.sender.username : null;
  const affected = new Set(caseDetails.affectedItemIds ?? []);
  const affectedItems = trade.items.filter((item) => item.id && affected.has(item.id));
  // Days since ship; before shipping, days since the trade was created
  // (Eric, 2026-10-28: nothing records a delivery).
  const shipDays = daysAfter(trade.shippedAt, caseDetails.createdAt);
  const createdDays = trade.shippedAt ? null : daysAfter(trade.createdAt, caseDetails.createdAt);
  const images = caseDetails.images ?? [];

  return (
    <section className="why-summoned-panel" data-testid="why-summoned-panel">
      <h2 className="panel-title">Why you were summoned</h2>
      {reason ? (
        <>
          <p className="why-summoned-reason">{caseReasonLongLabel(reason)}</p>
          <p className="why-summoned-meta" data-testid="why-summoned-meta">
            {username ? <strong>{username}</strong> : <strong>Unknown</strong>}
            {role && ` (${role.toLowerCase()})`}
            {caseDetails.source && SOURCE_LABEL[caseDetails.source] && ` · from the ${SOURCE_LABEL[caseDetails.source]} page`}
            {` · ${formatTimestamp(caseDetails.createdAt)}`}
            {shipDays !== null && ` · ${shipDays} day${shipDays === 1 ? '' : 's'} after ship`}
            {createdDays !== null && ` · ${createdDays} day${createdDays === 1 ? '' : 's'} after trade created`}
          </p>
          {affectedItems.length > 0 && (
            <div className="why-summoned-chips" aria-label="Affected cards">
              {affectedItems.map((item) => (
                <span key={item.id} className="why-summoned-chip">{item.name}</span>
              ))}
            </div>
          )}
          {caseDetails.description && (
            <blockquote className="why-summoned-description">{caseDetails.description}</blockquote>
          )}
          {images.length > 0 && (
            <div className="why-summoned-photos">
              {images.map((img, idx) => (
                <button key={img.id} type="button" className="why-summoned-photo" onClick={() => onOpenImage(images, idx)} aria-label={`Open attachment ${idx + 1}`}>
                  <img src={img.url} alt={`Attachment ${idx + 1}`} loading="lazy" />
                </button>
              ))}
            </div>
          )}
          <p className="why-summoned-also">
            {caseDetails.alsoRequestedBy
              ? `${otherParty ?? 'The other party'} has also requested an admin.`
              : `${otherParty ?? 'The other party'} has not requested an admin.`}
          </p>
        </>
      ) : (
        <p className="why-summoned-reason why-summoned-reason--none">{NO_REASON_LABEL}</p>
      )}
    </section>
  );
};

export default WhySummonedPanel;
